// ===== Sound: real sound files from audio/audio.json, otherwise synthesised with Web Audio (no speech) =====
import { Registry } from './registry';
import type { SoundType, SynthStyle } from './types';

type Wave = OscillatorNode['type'];
type Manifest = { sfx?: Record<string, string | string[]>; music: Record<string, string>; musicVolume?: number };
export const SFX = (() => {
  // ctx, master, sfxGain and musicGain exist once init() has run (called on first tap)
  let ctx: AudioContext = null!, master: GainNode = null!, sfxGain: GainNode = null!, musicGain: GainNode = null!, musicTimer: ReturnType<typeof setInterval> | null = null;
  const st: Record<string, boolean> = { sound: true, music: true };
  const files: Record<string, AudioBuffer[]> = {};            // name -> [AudioBuffer]
  const musicFiles: Record<string, string> = {};       // world -> file in audio/music/ (the world's meta.music); the menu is in audio.json
  let manifest: Manifest | null = null, loading: Promise<void> | null = null;
  let bgm: HTMLAudioElement | null = null, bgmNode: MediaElementAudioSourceNode | null = null, bgmFile = '', curKey = 'menu', fadeT: ReturnType<typeof setTimeout> | undefined;
  const lastAt: Record<string, number> = {};
  function init() {
    if (ctx) { if (ctx.state === 'suspended') ctx.resume(); applyMusic(); return; }
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)() as AudioContext;
      master = ctx.createGain(); master.gain.value = 0.7; master.connect(ctx.destination);
      sfxGain = ctx.createGain(); sfxGain.gain.value = 1; sfxGain.connect(master);
      musicGain = ctx.createGain(); musicGain.gain.value = 0.10; musicGain.connect(master);
    } catch (e) { ctx = null!; return; }
    loadFiles();
  }
  function loadFiles() {
    if (loading) return loading;
    loading = fetch('audio/audio.json').then(r => r.ok ? r.json() : null).then((m: Manifest | null) => {
      const man = manifest = m || { sfx: {}, music: {} };
      for (const [name, list] of Object.entries(man.sfx || {})) {
        files[name] = [];
        for (const f of ([] as string[]).concat(list)) fetch('audio/' + f).then(r => r.arrayBuffer())
          .then(b => new Promise<AudioBuffer>((ok, no) => ctx.decodeAudioData(b, ok, no)))
          .then(buf => files[name].push(buf)).catch(() => {});
      }
      applyMusic();
    }).catch(() => { manifest = { sfx: {}, music: {} }; });
    return loading;
  }
  const now = () => ctx.currentTime;
  function tone(f: number, d: number, type?: Wave | null, vol?: number | null, f2?: number | null, delay?: number | null, dest?: AudioNode) {
    if (!ctx) return;
    const t = now() + (delay || 0);
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.type = type || 'sine'; o.frequency.setValueAtTime(f, t);
    if (f2) o.frequency.exponentialRampToValueAtTime(f2, t + d);
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(vol || 0.3, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + d);
    o.connect(g); g.connect(dest || master); o.start(t); o.stop(t + d + 0.05);
  }
  function noise(d: number, vol?: number, fq?: number, q?: number, delay?: number) {
    if (!ctx) return;
    const t = now() + (delay || 0), n = Math.floor(ctx.sampleRate * d);
    const buf = ctx.createBuffer(1, n, ctx.sampleRate), data = buf.getChannelData(0);
    for (let i = 0; i < n; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / n);
    const src = ctx.createBufferSource(); src.buffer = buf;
    const f = ctx.createBiquadFilter(); f.type = 'bandpass'; f.frequency.value = fq || 1200; f.Q.value = q || 1;
    const g = ctx.createGain(); g.gain.value = vol || 0.3;
    src.connect(f); f.connect(g); g.connect(master); src.start(t);
  }
  // Synthetic fallback sounds, used when there is no sound file
  const lib: Record<string, (v: number) => void> = {
    click: () => tone(900, 0.06, 'triangle', 0.18),
    pick: () => tone(520, 0.08, 'sine', 0.2, 780),
    place: () => { tone(300, 0.1, 'sine', 0.3, 180); noise(0.05, 0.15, 800); },
    snap: () => { tone(1200, 0.05, 'square', 0.08); tone(1600, 0.06, 'square', 0.06, null, 0.05); },
    remove: () => tone(500, 0.15, 'sine', 0.2, 200),
    rotate: () => tone(700, 0.05, 'triangle', 0.12, 900),
    nope: () => { tone(220, 0.12, 'square', 0.08); tone(180, 0.15, 'square', 0.08, null, 0.1); },
    boing: () => { tone(180, 0.35, 'sine', 0.35, 620); tone(360, 0.25, 'triangle', 0.1, 900); },
    thunk: (v) => { tone(90, 0.25, 'sine', Math.min(0.5, 0.15 + v * 0.03), 45); noise(0.12, 0.2, 300); },
    tap: (v) => tone(1400 + Math.random() * 400, 0.04, 'sine', Math.min(0.12, 0.02 + v * 0.01)),
    toast: () => { tone(160, 0.2, 'sawtooth', 0.06, 120); noise(0.1, 0.1, 2000, 3); },
    pop: () => { tone(1320, 0.25, 'sine', 0.3); tone(1980, 0.4, 'sine', 0.12, null, 0.02); tone(250, 0.2, 'sine', 0.25, 700); },
    click2: () => { tone(1800, 0.03, 'square', 0.1); tone(600, 0.06, 'sine', 0.2); },
    snip: () => { noise(0.05, 0.4, 5000, 4); noise(0.05, 0.4, 5000, 4, 0.08); },
    plop: () => { tone(400, 0.12, 'sine', 0.35, 900); tone(250, 0.18, 'sine', 0.2, null, 0.05); },
    splat: () => { noise(0.25, 0.35, 500, 0.7); tone(200, 0.2, 'sine', 0.2, 90); },
    creak: () => tone(140 + Math.random() * 30, 0.08, 'sawtooth', 0.03),
    duck: () => { tone(1100, 0.08, 'square', 0.12, 1500); tone(1300, 0.1, 'square', 0.1, 900, 0.09); },
    bell: () => { [1047, 1319, 1568, 2093].forEach((f, i) => tone(f, 1.6, 'sine', 0.22 / (i + 1), null, i * 0.01)); },
    win: () => { [523, 659, 784, 1047, 1319].forEach((f, i) => tone(f, 0.35, 'triangle', 0.22, null, i * 0.09)); },
    star: (i) => tone([784, 988, 1175][i || 0], 0.35, 'sine', 0.3),
    fail: () => { tone(392, 0.2, 'triangle', 0.2); tone(330, 0.3, 'triangle', 0.2, null, 0.18); tone(262, 0.45, 'triangle', 0.2, 230, 0.36); },
    wiii: () => tone(600, 0.5, 'sine', 0.08, 1400),
    start: () => { tone(440, 0.1, 'triangle', 0.2); tone(660, 0.14, 'triangle', 0.2, null, 0.08); },
  };
  // Volume per sound file and minimum time between repeats (sec.)
  const VOL: Record<string, number> = { click: .5, pick: .5, place: .6, snap: .5, remove: .5, rotate: .4, nope: .45, tap: .35, creak: .5, star: .6, win: .7, fail: .5 };
  const GAP: Record<string, number> = { tap: .07, creak: 1.2, thunk: .08, click: .04 };
  function playFile(name: string, v: number) {
    const list = files[name]; if (!list || !list.length) return false;
    const t = now(); if (GAP[name] && t - (lastAt[name] || 0) < GAP[name]) return true; lastAt[name] = t;
    const src = ctx.createBufferSource(); src.buffer = list[Math.floor(Math.random() * list.length)];
    let rate = 0.96 + Math.random() * 0.08;
    if (name === 'star') rate = [1, 1.12, 1.26][v || 0];
    src.playbackRate.value = rate;
    const g = ctx.createGain(); let vol = VOL[name] ?? 0.7;
    if (name === 'thunk') vol = Math.min(0.9, 0.3 + v * 0.06);
    if (name === 'tap') vol = Math.min(0.5, 0.12 + v * 0.04);
    g.gain.value = vol; src.connect(g); g.connect(sfxGain); src.start();
    return true;
  }
  function play(name: SoundType, v?: number) { if (!st.sound || !ctx) return; try { if (!playFile(name, v || 0)) lib[name] && lib[name](v || 0); } catch (e) {} }

  // ---- Music: one track per world (audio/music/...), otherwise calm synthetic music (60–90 bpm) ----
  // Each world has its own mood: tempo, key, scale, chords and instrument.
  const SC: Record<SynthStyle['sc'], number[]> = { major: [0, 2, 4, 5, 7, 9, 11], minor: [0, 2, 3, 5, 7, 8, 10], dorian: [0, 2, 3, 5, 7, 9, 10], lydian: [0, 2, 4, 6, 7, 9, 11], mixolydian: [0, 2, 4, 5, 7, 9, 10] };
  // The menu's mood; each world has its own in meta.synth (bpm, root note, scale, chords, beats per bar, instrument, density, 7ths)
  const MENU_STYLE: SynthStyle = { bpm: 66, root: 261.6, sc: 'major',    prog: [0, 5, 3, 4], bar: 4, lead: 'bell',  dens: 0.35 };
  let synthBus: BiquadFilterNode = null!, nextT = 0, beat = 0, mel = 4, synthKey = '';
  function hz(sty: SynthStyle, deg: number, oct?: number) {
    const sc = SC[sty.sc], o = Math.floor(deg / 7) + (oct || 0);
    return sty.root * Math.pow(2, (sc[((deg % 7) + 7) % 7] + 12 * o) / 12);
  }
  // soft tone with slow attack and fade-out (no sharp edges)
  function note(f: number, t: number, d: number, type: Wave, vol: number, att?: number) {
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.type = type; o.frequency.value = f;
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(vol, t + (att || 0.02));
    g.gain.setTargetAtTime(0, t + (att || 0.02), d / 3);
    o.connect(g); g.connect(synthBus); o.start(t); o.stop(t + d + 0.5);
  }
  function lead(sty: SynthStyle, f: number, t: number, spb: number) {
    if (sty.lead === 'bell') { note(f, t, spb * 2.5, 'sine', 0.22, 0.01); note(f * 2, t, spb * 0.8, 'sine', 0.05, 0.01); }
    else if (sty.lead === 'pluck') note(f, t, spb * 1.2, 'triangle', 0.2, 0.01);
    else if (sty.lead === 'pizz') note(f, t, spb * 0.5, 'triangle', 0.22, 0.005);
    else note(f, t, spb * 1.6, 'sine', 0.2, 0.08);
  }
  function schedule() {
    if (!ctx || !st.music) return;
    // the world's own mood (meta.synth), otherwise the menu's
    const sty = (Registry.worlds[curKey] && Registry.worlds[curKey].meta && Registry.worlds[curKey].meta!.synth) || MENU_STYLE, spb = 60 / sty.bpm;
    if (synthKey !== curKey) { synthKey = curKey; beat = 0; mel = 4; }
    while (nextT < now() + 0.3) {
      const t = nextT, inBar = beat % sty.bar, chord = sty.prog[Math.floor(beat / sty.bar) % sty.prog.length];
      const tones = [chord, chord + 2, chord + 4].concat(sty.sev ? [chord + 6] : []);
      if (inBar === 0) {
        // chord pad over the whole bar + bass
        tones.forEach(d => note(hz(sty, d, -1), t, spb * sty.bar * 1.1, 'sine', 0.07, spb * 0.6));
        note(hz(sty, chord, -2), t, spb * 2, 'sine', 0.22, 0.03);
      } else if (sty.bar === 3 || inBar === 2) note(hz(sty, chord, -2) * (sty.bar === 3 ? 1.5 : 1), t, spb * 1.2, 'sine', 0.12, 0.03);
      // melody: small steps in the scale, lands on chord tones, with rests
      for (const half of [0, 1]) {
        if (Math.random() > sty.dens * (half ? 0.5 : 1)) continue;
        mel += [-2, -1, -1, 0, 1, 1, 2][Math.floor(Math.random() * 7)];
        if (mel < 0) mel = 1; if (mel > 9) mel = 8;
        if (!half && inBar === 0) mel = tones[Math.floor(Math.random() * 3)] % 7 + (mel > 6 ? 7 : 0);
        lead(sty, hz(sty, mel, 0), t + half * spb / 2, spb);
      }
      beat++; nextT += spb;
    }
  }
  function synth(on: boolean) {
    if (on && !musicTimer) {
      if (!synthBus) {
        synthBus = ctx.createBiquadFilter(); synthBus.type = 'lowpass'; synthBus.frequency.value = 2400; synthBus.connect(musicGain);
      }
      musicGain.gain.cancelScheduledValues(now()); musicGain.gain.setValueAtTime(0, now());
      musicGain.gain.linearRampToValueAtTime(0.26, now() + 1.5);
      nextT = now() + 0.1; synthKey = ''; musicTimer = setInterval(schedule, 100); schedule();
    }
    if (!on && musicTimer) { clearInterval(musicTimer); musicTimer = null; }
  }
  function fileFor(key: string) { const m = manifest && manifest.music; return m ? ((key in m ? m[key] : musicFiles[key]) || '') : ''; }
  function fadeTo(val: number, then?: () => void) {
    clearTimeout(fadeT); musicGain.gain.cancelScheduledValues(now());
    musicGain.gain.setValueAtTime(musicGain.gain.value, now()); musicGain.gain.linearRampToValueAtTime(val, now() + 0.6);
    if (then) fadeT = setTimeout(then, 650);
  }
  function applyMusic() {
    if (!ctx || !manifest) return;
    const file = st.music ? fileFor(curKey) : '';
    const el = bgm;
    if (!st.music) { synth(false); if (el && !el.paused) fadeTo(0, () => el.pause()); return; }
    if (!file) { if (bgm && !bgm.paused) bgm.pause(); bgmFile = ''; synth(true); return; }
    synth(false);
    const man = manifest;
    if (!bgm) {
      const b = bgm = new Audio(); b.loop = true; b.preload = 'auto'; b.setAttribute('playsinline', '');
      b.addEventListener('error', () => { if (bgmFile) { man.music[curKey] = ''; bgmFile = ''; applyMusic(); } });
      try { bgmNode = ctx.createMediaElementSource(b); bgmNode.connect(musicGain); } catch (e) {}
    }
    const a = bgm, vol = (man.musicVolume || 0.35);
    if (bgmFile === file) { if (a.paused) { musicGain.gain.value = 0; a.play().catch(() => {}); } fadeTo(vol); return; }
    const swap = () => { bgmFile = file; a.src = 'audio/music/' + file; musicGain.gain.value = 0; a.play().catch(() => {}); fadeTo(vol); };
    if (bgmFile && !a.paused) fadeTo(0, swap); else swap();
  }
  function track(key?: string | null) { key = key || 'menu'; if (key === curKey) return; curKey = key; applyMusic(); }
  function music(on: boolean) { st.music = on; applyMusic(); if (!manifest && ctx) synth(on); }
  // The world's music file (meta.music), set by the game when the worlds are loaded
  function defineMusic(key: string, file?: string) { if (file) musicFiles[key] = file; }
  // Worlds can add their own synthetic sounds: define('name', (tone, noise, v) => {...})
  function define(name: string, fn: (t: typeof tone, n: typeof noise, v: number) => void) { if (!lib[name]) lib[name] = v => fn(tone, noise, v); }
  return { init, play, music, track, define, defineMusic, set: (k: string, v: boolean) => { st[k] = v; if (k === 'music') music(v); }, get st() { return st; } };
})();
